import { isAxiosError } from "axios";
import { format } from "date-fns";
import i18n, { t } from "i18next";
import axios from "../lib/axios";

export interface PagedWorkPlanResult {
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  items: WorkPlan[];
}

export interface WorkPlan {
  employee: {
    id: number;
    companyId: number;
    contractType: number;
    code: string;
    firstName: string;
    lastName: string;
    isBorrowed: boolean;
    borrowedByManagerId?: number;
    borrowedByManagerCompanyId?: number;
    startDateOfTheRental?: string | null;
    endDateOfTheRental?: string | null;
  };
  sum: {
    sumaDni: number;
    sumaGodzin: number;
  };
  summary?: {
    sumaGodzin: number;
    sumaDni: number;
  };
  days: {
    date: string;
    nameType: number | null;
    dayType: number | null;
    hours: {
      fromHour: string;
      toHour: string;
      duration: string;
      note: string;
      project: string;
    };
  }[];
  violations: {
    affectedDays: string[];
    message: string;
  }[];
}

export interface AnnualLeaveEntry {
  employee: {
    id: number;
    companyId: number;
    contractType: number;
    code: string;
    firstName: string;
    lastName: string;
    isBorrowed: boolean;
    borrowedByManagerId?: number;
    borrowedByManagerCompanyId?: number;
    startDateOfTheRental?: string | null;
    endDateOfTheRental?: string | null;
  };
  sum: {
    sumaDni: number;
    sumaGodzin: number;
  };
  summary?: {
    sumaGodzin: number;
    sumaDni: number;
  };
  remainingAnnualLeave?: {
    hours: number;
    days: number;
  } | null;
  days: {
    date: string;
    nameType: number | null;
    dayType: number | null;
    hours: {
      fromHour: string;
      toHour: string;
      duration: string;
      note: string;
      project: string;
    };
  }[];
  violations: {
    affectedDays: string[];
    message: string;
  }[];
}

export interface PagedWorkPlanResult {
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  items: WorkPlan[];
}

export async function downloadCalendarData(
  year?: number,
  month?: number,
  employeeId?: number,
) {
  try {
    const response = await axios.get(`/api/work-plan-for-authenticated-user`, {
      params: {
        year,
        month,
        employeeId,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function sendAvailability(
  employeeId: number[],
  companyId: number[],
  dates: Date[],
  skipDaysOff: boolean,
  note?: string,
  positions: { id?: number; from: string; to: string }[] = [],
) {
  try {
    const formattedDates = dates.map((date) =>
      format(date, "yyyy-MM-dd'T'00:00:00"),
    );
    const requestData = {
      employeeId,
      companyId,
      dates: formattedDates,
      skipDaysOff,
      note,
      positions,
    };

    const response = await axios.post(`/api/availabilities`, requestData, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data.message || t("Availability created succesfull."),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function deleteAvailability(date: Date) {
  try {
    const formattedDate = format(date, "yyyy-MM-dd'T'00:00:00");
    const response = await axios.delete(`/api/availabilities`, {
      params: { date: formattedDate },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data?.message,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function downloadAvailability(
  year?: number,
  month?: number,
  companyIds?: number[],
  employeeIds?: number[],
) {
  const ceids = (companyIds ?? []).map((companyId, index) => ({
    companyId,
    employeeId: (employeeIds ?? [])[index] ?? 0,
  }));

  try {
    const response = await axios.post(`/api/availabilities/for-users`, ceids, {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        year,
        month,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ??
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function downloadAbsenceType() {
  try {
    const response = await axios.get(`/api/absence-typ`, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });
    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function downloadAbsenceTypeUser() {
  try {
    const response = await axios.get(`/api/absence-typ-user`, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function downloadAbsence(
  year?: number,
  month?: number,
  employeeIds?: number[],
  companyIds?: number[],
) {
  const ceids = (companyIds ?? []).map((companyId, index) => ({
    companyId,
    employeeId: (employeeIds ?? [])[index] ?? 0,
  }));

  try {
    const response = await axios.post(`/api/absence-user`, ceids, {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        year,
        month,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function sendAbsence(
  reportingEmployeeId: number,
  reportingCompanyId: number,
  from: Date,
  to: Date,
  name: string,
  skipDaysOff: boolean,
  confirmed: number,
  leaveForAssignment?: number,
  note?: string,
  employeeId?: number[],
  companyId?: number[],
  type?: number,
) {
  try {
    const response = await axios.post(
      `/api/absence`,
      {
        reportingEmployeeId,
        reportingCompanyId,
        from: format(from, "yyyy-MM-dd'T'00:00:00"),
        to: format(to, "yyyy-MM-dd'T'00:00:00"),
        name,
        skipDaysOff,
        leaveForAssignment,
        confirmed,
        note,
        employeeId,
        companyId,
        type,
      },
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );
    return {
      isOk: true,
      message: response.data.message || t("Absence created succesfull."),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function confirmedAbsence(id: number) {
  try {
    const response = await axios.post(`/api/absence-confirm?id=${id}`, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data.message || t("Absence confirmed succesfull."),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function deleteAbsence(id: number) {
  try {
    const response = await axios.put(`/api/absence-delete`, null, {
      params: { id },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message: response.data.message || t("Absence deleted succesfull."),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export const getAnnualLeaveLimit = async (
  year?: number,
  month?: number,
): Promise<{
  remainingAnnualLeaveDays: number;
  remainingAnnualLeaveHours: number;
  unconfirmedAnnualLeaveDays: number;
  unconfirmedAnnualLeaveHours: number;
}> => {
  try {
    const { data } = await axios.get<{
      remainingAnnualLeaveDays: number;
      remainingAnnualLeaveHours: number;
      unconfirmedAnnualLeaveDays: number;
      unconfirmedAnnualLeaveHours: number;
    }>(`/api/holiday-limit`, {
      params: {
        year,
        month,
      },
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

interface FetchWorkPlanResult {
  isOk: boolean;
  data?: PagedWorkPlanResult;
  error?: string;
}

export async function fetchWorkPlan(
  fromDate: string,
  toDate: string,
  companyId: number,
  employeeId: number,
  pageNumber: number,
  pageSize: number,
  onlyContract20: boolean = false,
  search?: string,
): Promise<FetchWorkPlanResult> {
  try {
    const response = await axios.get<PagedWorkPlanResult>(
      `/api/companies/${companyId}/employees/${employeeId}/work-plan`,
      {
        params: {
          fromDate,
          toDate,
          pageNumber,
          pageSize,
          onlyContract20,
          ...(search ? { search } : {}),
        },
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function fetchSubordinateEmployees(
  companyId: number,
  employeeId: number,
  includeHistoricalBorrowed: boolean = true,
  from?: string,
  to?: string,
) {
  try {
    const response = await axios.get(
      `/api/subordinate-employees/companies/${companyId}/employeeId/${employeeId}/employees`,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          includeHistoricalBorrowed,
          from,
          to,
        },
      },
    );

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function fetchSubordinateManagersForManager() {
  try {
    const response = await axios.get(`/api/awards/subordinate-managers`, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function fetchSubordinateEmployeesWithLoaned(
  companyId: number,
  employeeId: number,
  from?: string,
  to?: string,
) {
  try {
    const response = await axios.get(
      `/api/subordinate-employees-with-loaned/companies/${companyId}/employeeId/${employeeId}/employees`,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          from,
          to,
        },
      },
    );

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function AbsenceToConfirm(
  companyId?: number,
  from?: string,
  to?: string,
) {
  try {
    const response = await axios.get(`/api/absence-to-be-confirmed`, {
      params: {
        companyId,
        from,
        to,
      },

      headers: {
        "Accept-Language": i18n.language,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function downloadProjects(companyId?: number | null) {
  try {
    const response = await axios.get(`/api/projects`, {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        companyId,
      },
    });

    return {
      isOk: true,
      message:
        response.data.message ||
        t("Information download completed successfully"),
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}

export async function fetchWorkPlanForManager(
  fromDate,
  toDate,
  pageNumber,
  pageSize,
) {
  try {
    const year = new Date(fromDate).getFullYear();
    const month = new Date(fromDate).getMonth() + 1;

    const response = await axios.get(
      `/api/work-plan-for-department-employees-manager`,
      {
        params: {
          year,
          month,
          pageIndex: pageNumber ?? 1,
          pageSize: pageSize ?? 50,
        },
      },
    );

    return {
      isOk: true,
      data: response.data,
    };
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
}
